import type { ScormData, ScormInteractionData } from '~/utils/scorm'

/** Une interaction enregistrée, telle que l'API la rend (#158). */
type StoredInteraction = {
  scoIdentifier?: string
  index: number
  interactionId: string
  type?: string | null
  description?: string | null
  correctResponses: string[]
  learnerResponse?: string | null
  result?: string | null
  weighting?: number | null
  latency?: string | null
  timestamp?: string | null
  objectiveIds: string[]
}

type AttemptState = {
  completionStatus: string
  successStatus: string
  scoreRaw?: number | null
  scoreMin?: number | null
  scoreMax?: number | null
  scoreScaled?: number | null
  totalTime?: string | null
  interactions?: StoredInteraction[]
  scoStates?: ScoState[]
}

/** L'état d'un chapitre dans la tentative en cours (#160). */
type ScoState = {
  scoIdentifier: string
  completionStatus: string
  successStatus: string
  scoreRaw?: number | null
  scoreMin?: number | null
  scoreMax?: number | null
  scoreScaled?: number | null
  totalTime?: string | null
  suspendData?: string | null
  location?: string | null
}

type TrackingState = {
  totalTime?: string | null
  sessionTime?: string | null
  suspendData?: string | null
  location?: string | null
  currentAttempt?: AttemptState | null
}

/** Les champs renseignés d'une interaction enregistrée, dans le format du bridge. */
function toInteraction({ interactionId, correctResponses, objectiveIds, ...rest }: StoredInteraction): ScormInteractionData {
  const interaction: ScormInteractionData = { index: rest.index, id: interactionId }
  for (const field of ['type', 'description', 'learnerResponse', 'result', 'latency', 'timestamp'] as const) {
    const value = rest[field]
    if (value) interaction[field] = value
  }
  if (rest.weighting != null) interaction.weighting = rest.weighting
  if (correctResponses.length > 0) interaction.correctResponses = correctResponses
  if (objectiveIds.length > 0) interaction.objectiveIds = objectiveIds
  return interaction
}

/**
 * L'état SCORM dont le lecteur repart (#84).
 *
 * Statut et score viennent de la TENTATIVE en cours, et non des colonnes du
 * suivi, qui portent le score RETENU (le meilleur, par exemple) : reprendre un
 * passage avec le score d'un autre ferait croire au paquet qu'il est déjà fait.
 *
 * Pas de tentative en cours — tout premier lancement, ou nouvelle tentative qui
 * vient de démarrer — : rien à reprendre, le paquet démarre vierge (`ab-initio`).
 */
export function playerInitialData(tracking: TrackingState | null | undefined): ScormData | null {
  const attempt = tracking?.currentAttempt
  if (!tracking || !attempt) return null
  return {
    completionStatus: attempt.completionStatus,
    successStatus: attempt.successStatus,
    scoreRaw: attempt.scoreRaw,
    scoreMin: attempt.scoreMin,
    scoreMax: attempt.scoreMax,
    scoreScaled: attempt.scoreScaled,
    // Le temps de CETTE tentative : le suivi porte le cumul de toutes, et le
    // paquet y ajouterait son passage avant de le renvoyer — compté deux fois.
    totalTime: attempt.totalTime,
    sessionTime: tracking.sessionTime,
    suspendData: tracking.suspendData,
    location: tracking.location,
    // Les réponses déjà données dans cette tentative (#158).
    interactions: attempt.interactions?.map(toInteraction),
  }
}

/**
 * L'état dont un chapitre repart (#160), pour un module à plusieurs SCO : le
 * sien dans la tentative en cours, avec ses seules réponses. Chapitre jamais
 * ouvert, ou nouvelle tentative : il démarre vierge.
 */
export function scoInitialData(tracking: TrackingState | null | undefined, scoIdentifier: string): ScormData | null {
  const attempt = tracking?.currentAttempt
  const state = attempt?.scoStates?.find(candidate => candidate.scoIdentifier === scoIdentifier)
  if (!attempt || !state) return null
  return {
    completionStatus: state.completionStatus,
    successStatus: state.successStatus,
    scoreRaw: state.scoreRaw,
    scoreMin: state.scoreMin,
    scoreMax: state.scoreMax,
    scoreScaled: state.scoreScaled,
    totalTime: state.totalTime,
    suspendData: state.suspendData,
    location: state.location,
    interactions: attempt.interactions?.filter(interaction => interaction.scoIdentifier === scoIdentifier).map(toInteraction),
  }
}
